import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@wally-reseller/ui/button';
import { Input, Label } from '@wally-reseller/ui/input';
import { MailCheck } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { api, ApiError } from '@/lib/api';
import { useLocale, useT } from '@/lib/i18n';
import { LanguageSwitch } from './LanguageSwitch';
import { AFTER_LOGIN_KEY } from './Layout';

/** The page the person wanted before logging in (within the hour); only our own paths. */
function afterLogin(): string {
  try {
    const saved = JSON.parse(localStorage.getItem(AFTER_LOGIN_KEY) ?? 'null') as {
      path?: unknown;
      at?: unknown;
    } | null;
    localStorage.removeItem(AFTER_LOGIN_KEY);
    const { path, at } = saved ?? {};
    const fresh = typeof at === 'number' && Date.now() - at < 3600_000;
    if (fresh && typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')) {
      return path;
    }
  } catch {
    /* storage unavailable */
  }
  return '/';
}

const TOKEN = /^[A-Za-z0-9_-]{43}$/;

/**
 * Passwordless login. The emailed link is /login#<token>: the token sits in the fragment (never
 * sent to a server or a Referer) and is only used when the person clicks the button, so mail
 * scanners that open links cannot burn it.
 */
export function LoginPage() {
  const [token] = useState(() => {
    const fragment = window.location.hash.slice(1);
    if (fragment) history.replaceState(null, '', window.location.pathname);
    return TOKEN.test(fragment) ? fragment : null;
  });
  return <Card>{token ? <Verify token={token} /> : <RequestLink />}</Card>;
}

function Card({ children }: { children: ReactNode }) {
  const t = useT();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-subtle px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/wally-128.png" alt="" width={28} height={28} className="rounded-full" />
            <span className="text-sm font-semibold">{t('portal.brand')}</span>
          </div>
          <LanguageSwitch />
        </div>
        <div className="rounded-lg border border-border bg-background p-6 shadow-sm">
          {children}
        </div>
      </div>
    </div>
  );
}

function RequestLink() {
  const t = useT();
  const { locale } = useLocale();
  const [email, setEmail] = useState('');
  const send = useMutation({ mutationFn: () => api.requestLink({ email, locale }) });

  if (send.isSuccess) {
    return (
      <div className="space-y-3">
        <MailCheck className="size-6 text-success" />
        <h1 className="text-lg font-semibold">{t('portal.login.sent.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('portal.login.sent.body', { email })}</p>
        <Button variant="link" className="px-0" onClick={() => send.reset()}>
          {t('portal.login.again')}
        </Button>
      </div>
    );
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    send.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">{t('portal.login.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('portal.login.body')}</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="email">{t('portal.login.email')}</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      {send.isError && <ErrorLine error={send.error} />}
      <Button type="submit" size="lg" className="w-full" disabled={send.isPending}>
        {t('portal.login.submit')}
      </Button>
    </form>
  );
}

function Verify({ token }: { token: string }) {
  const t = useT();
  const { setLocale } = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const verify = useMutation({
    mutationFn: () => api.verify(token),
    onSuccess: (me) => {
      setLocale(me.user.locale);
      queryClient.setQueryData(['me'], me);
      navigate(afterLogin(), { replace: true });
    },
  });

  if (verify.isError) {
    return (
      <div className="space-y-3">
        <h1 className="text-lg font-semibold">{t('portal.verify.title')}</h1>
        {verify.error instanceof ApiError && verify.error.status === 401 ? (
          <p className="text-sm text-danger">{t('portal.verify.invalid')}</p>
        ) : (
          <ErrorLine error={verify.error} />
        )}
        <Button variant="outline" onClick={() => navigate('/login', { replace: true })}>
          {t('portal.login.submit')}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">{t('portal.verify.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('portal.verify.body')}</p>
      </div>
      <Button
        size="lg"
        className="w-full"
        disabled={verify.isPending}
        onClick={() => verify.mutate()}
      >
        {t('portal.verify.submit')}
      </Button>
    </div>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const t = useT();
  const tooMany = error instanceof ApiError && error.status === 429;
  return (
    <p className="text-sm text-danger">
      {t(tooMany ? 'portal.error.tooMany' : 'portal.error.generic')}
    </p>
  );
}
